import { ComponentHeader } from '@/catalog-apps/components/component-library/shared/ComponentHeader'
import { ComponentTitle } from '@/catalog-apps/components/component-library/shared/ComponentTitle'
import { SearchInput } from '@/catalog-apps/components/component-library/shared/SearchInput'
import { COMPONENT_HEADER_HEIGHT, MAIN_HEADER_HEIGHT, PAGE_HEADER_HEIGHT } from '@/catalog-apps/components/config'
import { Button } from '@/components/shadcn/button'
import { Skeleton } from '@/components/shadcn/skeleton'
import { NoSearchResultsDisplay } from '@/components/ui/NoSeachResultsDisplay'
import { IMG_BASE_PATH } from '@/const'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { useVirtualizer } from '@tanstack/react-virtual'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { AddImageFormV2 } from '../Forms'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { toast } from 'sonner'

type MessagesSupportImageItem = RestSchemas['Messages.Support.ImageItem']

export function ImagesGridV2() {
  const { getSupportApiListImages } = useRestClient()
  const { data: images } = getSupportApiListImages.useSuspenseQuery()
  const [searchString, setSearchString] = useState(() => '')

  const filteredImages = useMemo(() => {
    return searchString
      ? images?.filter((image) => image.filename?.toLowerCase().includes(searchString.toLowerCase()))
      : images
  }, [images, searchString])

  const parentRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)

  useEffect(() => {
    if (!parentRef.current) return
    const observer = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width)
    })
    observer.observe(parentRef.current)
    return () => observer.disconnect()
  }, [])

  const numColumns = Math.max(1, Math.floor((containerWidth + 16) / 316))
  const gap = 16
  const rowHeight = 300
  const rowSize = rowHeight + gap
  const rowsCount = Math.ceil((filteredImages?.length || 0) / numColumns)

  const rowVirtualizer = useVirtualizer({
    count: rowsCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowSize,
    overscan: 5,
  })

  return (
    <>
      <ComponentHeader className="border-b-0 pt-4">
        <ComponentTitle title="Images" />
        <div className="ml-auto mr-0 flex items-center gap-4">
          <div className="flex items-center gap-x-2">
            <SearchInput value={searchString} onChange={setSearchString} />
            <AddImageFormV2
              sheetTitle="Add Image"
              sheetDescription="Upload a new image to the platform"
              sheetTriggerTooltip="Add Image"
              sheetTrigger={
                <Button variant="outline" size="icon" className="size-9 rounded-full">
                  <IconPlusOutline />
                  <span className="sr-only">Add Image</span>
                </Button>
              }
              onSubmit={() => {
                // Refetch will happen automatically via query invalidation
              }}
            />
          </div>
        </div>
      </ComponentHeader>
      {filteredImages?.length > 0 ? (
        <div
          ref={parentRef}
          style={{
            height: `calc(100vh - ${MAIN_HEADER_HEIGHT * 2}px - ${PAGE_HEADER_HEIGHT}px - ${COMPONENT_HEADER_HEIGHT}px)`,
            overflowY: 'auto',
            contain: 'strict',
          }}
          className="pb-5"
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const startIndex = virtualRow.index * numColumns
              const rowImages = filteredImages.slice(startIndex, startIndex + numColumns)

              return (
                <div
                  key={virtualRow.index}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${rowHeight}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                    display: 'grid',
                    gridTemplateColumns: `repeat(${numColumns}, 1fr)`,
                    gap: `${gap}px`,
                  }}
                >
                  {rowImages.map((image) => (
                    <ImageCard key={image.filename} image={image} />
                  ))}
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <NoSearchResultsDisplay text="No images found matching your search" />
      )}
    </>
  )
}

const ImageCard = memo(function ImageCard({ image }: { image: MessagesSupportImageItem }) {
  const imageKey = image.filename ?? ''
  const restClient = useRestClient()
  const { deleteSupportApiDeleteImage, getSupportApiListImages } = restClient
  const invalidateImages = () => getSupportApiListImages.invalidateQueries()
  const { mutateAsync: deleteImage, isPending: isDeleting } = deleteSupportApiDeleteImage.useMutation()

  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Handle the delete action
  const handleDeleteImage = async () => {
    try {
      await deleteImage({
        query: { imageKey },
      })
      toast.success('Image deleted successfully.')
      invalidateImages()
      setDeleteDialogOpen(false)
    } catch (error) {
      console.error('Error deleting image:', error)
      toast.error('Error deleting image.')
    }
  }
  return (
    <div className="relative h-[300px]">
      <img
        className="h-full w-full object-cover"
        src={`${IMG_BASE_PATH}${image.filename}`}
        alt={image.filename || undefined}
        loading="lazy"
        decoding="async"
      />
      <div className="absolute bottom-0 left-0 flex h-8 w-full items-center justify-between bg-background/80 px-3 backdrop-blur">
        <span className="truncate">{image.filename}</span>
        <ConfirmDialog
          isOpen={isDeleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          triggerTooltip="Delete Image"
          title="Delete Image"
          description="Are you sure you want to delete this image?"
          trigger={
            <Button
              variant="ghost"
              size="icon"
              className="size-6 rounded-full hover:bg-transparent hover:text-destructive"
            >
              <IconDeleteOutline />
              <span className="sr-only">Delete Image</span>
            </Button>
          }
          onConfirm={handleDeleteImage}
          isSubmitting={isDeleting}
        />
      </div>
    </div>
  )
})

export function ImagesLoading() {
  return (
    <>
      <ComponentHeader className="border-b-0 pt-5">
        <div className="ml-auto mr-0 flex items-center gap-4">
          <div className="flex items-center gap-x-2">
            <Skeleton className="h-[35px] w-[150px]" />
          </div>
        </div>
      </ComponentHeader>
      <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4 pb-5">
        {Array.from({ length: 16 }).map((_, index) => (
          <Skeleton key={index} className="h-[300px] w-full rounded-xl" />
        ))}
      </div>
    </>
  )
}
